import { PROFILE_URL_PATH, STATUSPAGE } from '@repo/ui';
import {
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
  GetByIdProfessionalManagementUseCaseInterface,
  GetProfessionalOverviewUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearProfessionalOverviewData,
  clearSelectedProfessionalData,
  loadProfessionalOverviewData,
  loadSelectedProfessionalData,
} from 'presentation/config/store/professionals/professional-overview-slice';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createSelector } from 'reselect';
import * as Yup from 'yup';

export const useProfessionalOverview = () => {
  // Hooks
  const navigate = useNavigate();
  const { professionalService } = useService();
  const { professionalType, id } = useParams<{
    professionalType: PROFILE_URL_PATH;
    id: string;
  }>();
  const { pathname } = useLocation();
  const { getLegends } = useLegends();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const { filterBySemester, filterByCurricularUnit } = currentUser;
  const { groupService, curricularUnitService, semesterService } = useService();
  const layout = createSelector(
    (state) => state.Layout,
    (layoutModeType) => layoutModeType.layoutModeType,
  );
  const layoutModeType = useSelector(layout);

  // Constants
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const firstDayOfMonth = new Date(
    currentDay.getFullYear(),
    currentDay.getMonth(),
    1,
  );
  const statusInitialStates = {
    professionalOverviewDataStatus: STATUSPAGE.DONE,
    groupsSelectStatus: STATUSPAGE.DONE,
    curricularUnitSelectStatus: STATUSPAGE.DONE,
    semesterSelectStatus: STATUSPAGE.DONE,
  };

  // States
  const { selectedProfessionalData, professionalOverviewData } = useAppSelector(
    (state) => state.professionalOverview,
  );
  const [groupsUser, setGroupsUser] =
    useState<GetAllGroupsUserUseCaseInterface.Result>([]);
  const [curricularUnitsUser, setCurricularUnitsUser] =
    useState<GetAllCurricularUnitsUserUseCaseInterface.Result>([]);
  const [semestersUser, setSemestersUser] =
    useState<GetAllSemestersUserUseCaseInterface.Result>([]);
  const [statusStates, setStatusStates] = useState(statusInitialStates);

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const requestGetProfessionalById = useCallback(
    async (params: GetByIdProfessionalManagementUseCaseInterface.Params) => {
      return await professionalService
        .getByIdProfessionalManagement(params)
        .then((res: GetByIdProfessionalManagementUseCaseInterface.Result) => {
          dispatch(loadSelectedProfessionalData(res));
        })
        .catch((err) => {
          dispatch(clearSelectedProfessionalData());
          notifyError(err.message || 'Erro ao buscar o profissional.');
        });
    },
    [],
  );

  const requestGetProfessionalOverview = useCallback(
    async (params: GetProfessionalOverviewUseCaseInterface.Params) => {
      changeStatusState('professionalOverviewDataStatus', STATUSPAGE.LOADING);
      await professionalService
        .getProfessionalOverview(params)
        .then((res) => {
          dispatch(loadProfessionalOverviewData(res));
        })
        .catch((err) => {
          dispatch(clearProfessionalOverviewData());
          notifyError(err.message || 'Erro ao buscar dados do profissional.');
        })
        .finally(() => {
          changeStatusState('professionalOverviewDataStatus', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllGroupsUser = useCallback(
    async (params?: GetAllGroupsUserUseCaseInterface.Params) => {
      await groupService
        .getAllGroupsUser(params)
        .then((res) => setGroupsUser(res))
        .catch((err) => console.log(err))
        .finally(() => {});
    },
    [],
  );

  const requestGetAllCurricularUnitsUser = useCallback(
    async (params?: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      await curricularUnitService
        .getAllCurricularUnitsUser(params)
        .then((res) => setCurricularUnitsUser(res))
        .catch((err) => console.log(err))
        .finally(() => {});
    },
    [],
  );

  const requestGetAllSemestersUser = useCallback(
    async (params?: GetAllSemestersUserUseCaseInterface.Params) => {
      await semesterService
        .getAllSemestersUser(params)
        .then((res) => setSemestersUser(res))
        .catch((err) => console.log(err))
        .finally(() => {});
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      semester: null,
      curricularUnit: null,
      groups: [],
      period: [firstDayOfMonth, currentDay],
    },
    validationSchema: Yup.object({
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      curricularUnit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      period: Yup.array().required('Período é obrigatório'),
    }),

    // There is no onSubmit function on this form (no submit button).
    // The API request is sent everytime any filter changes, so it is controlled via useEffect.
    onSubmit: () => {
      return;
    },
  });

  return {
    id,
    pathname,
    groupsUser,
    validation,
    statusStates,
    semestersUser,
    layoutModeType,
    professionalType,
    filterBySemester,
    curricularUnitsUser,
    selectedProfessionalData,
    professionalOverviewData,
    filterByCurricularUnit,
    navigate,
    getLegends,
    requestGetAllGroupsUser,
    requestGetAllSemestersUser,
    requestGetProfessionalById,
    requestGetProfessionalOverview,
    requestGetAllCurricularUnitsUser,
  };
};
